/* @flow */
/* eslint-env es6,browser */

/**
 * @typedef {object} globalParams
 * @typedef {object} adConfig
 * @typedef {object} jsonpConfig
 */

import * as u from './utils';
import * as jsonp from './jsonp';
import * as fetch from './utils/fetch';
import adrotation from './adrotation';
import bm from './bidmanager';
import adserver from './adserver';
import adloader from './adloader';
import storage from './utils/storage';
import adconfig from './adconfig';
import { logError } from './utils/logger';

/**
 * @description Implements ad configuration functionality
 * @exports adconfigUpdater
 * @requires  module:utils
 * @requires module:logger
 */

class _adconfigUpdater {
  _win: window;
  _doc: window.document;
  _expiryTimeoutSec: number;
  _loadTimeout: number;
  _url: string;
  _pageInfo: pageInfo;
  _lastConfigLoadTime: number;
  _gPageInfo: pageInfo;
  constructor() {
    this._win = window;
    this._doc = document;
    this._expiryTimeoutSec = 20 * 60;
    this._loadTimeout = 3000;
    this._url = '';
    this._lastConfigLoadTime = 0;
  }

  /**
   * Initialize the object
   *
   * @param {window} w - window object
   * @param {window.document} d - document object
   * @param {globalParams} g - global param object
   * @returns {undefined}
   */
  init(w: window, d: window.document, g: globalParam) {
    this._win = w;
    this._doc = d;
    this._loadTimeout = g.adConfig.loadTimeout;
    this._url = g.adConfig.url;
    adconfig.init(w, d, g);
  }

  /**
   * _processConfigData - take cares of updating caches and propagating config change
   *
   * @param {adConfig} data - AdConfig data
   * @param {siteInfo} info - Site info
   * @returns {undefined}
   * @access private
   */
  _processConfigData(data: adConfig, info: siteInfo) {
    adconfig.processConfigData(data);
    const config = adconfig.get();
    // update in-memory cache in ad config
    this._expiryTimeoutSec = config.settings.configExpirySec;

    // propagate config update to other classes
    adrotation.updateConfig(config.settings.adRotate);
    bm.updateConfig(config.settings.headerBidding);
    adserver.updateConfig(config.settings.adServer);
    adloader.updateConfig(config.settings.adloading);

    // update local storage cache
    storage.storeConfig(data, info.site);
  }

  /**
   * _fetchConfig - checks for fetch support and uses fetch instead of jsonp
   *
   * @param {string} url - URL for config
   * @param {jsonpConfig} params - Extra JSONP params
   * @returns {Promise} - Fetch/JSONP promise
   * @access private
   */
  _fetchConfig(url: string, params: jsonpConfig) {
    if (fetch.isSupported()) {
      params = params || {};
      params.data = params.data || {};
      params.data['format'] = 'json';
      return fetch.JSON(url, params);
    }
    return jsonp.JSONP(url, params);
  }

  /**
   * _load - load config from server
   *
   * @param {siteInfo} info - Site info
   * @param {boolean} bFirstLoad - Firstload?
   * @returns {Promise} - Config loading promise
   * @access private
   */
  _load(info: siteInfo, bFirstLoad: boolean) {
    const params = {};
    params.data = {
      site: info.site,
      apiVersion: u.apiVersion(),
      brand: info.brand
    };
    params.timeout = this._loadTimeout;
    params.logPrefix = 'adconfig';
    return this._fetchConfig(this._url, params).then(
      data => {
        if (data && data.settings) {
          this._processConfigData(data, info);
          this._lastConfigLoadTime = Date.now();
        }
        return data;
      },
      msg => {
        logError(
          `Failed to get ad config from ${this._url} for ${
            info.site
          } firstload:${bFirstLoad ? 1 : 0}: ${msg || ''}`
        );
      }
    );
  }

  /**
   * @global
   * @typedef {object} siteInfo
   * @property {string} site - site name
   * @property {string} brand - Brand
   * @property {string} [uid] - UserID
   * @property {string} [serviceLevel] - ServiceLevel
   */
  /**
   * loads adconfig from server. If available in cache, will return cache till expiry.
   *
   * @param {siteInfo} info - Site info
   * @returns {Promise<*>} - Promise
   */
  load(info: siteInfo): Promise<*> {
    const bFirstLoad = this._lastConfigLoadTime === 0;
    let expiredCache: boolean = true;
    // get from localstorage for first time
    if (bFirstLoad) {
      const { expired, data, updatedTime } = storage.getConfig(info.site);
      if (!expired && 'settings' in data) {
        this._processConfigData(data, info);
        if (Date.now() - updatedTime < this._expiryTimeoutSec * 1000) {
          this._lastConfigLoadTime = updatedTime;
          return Promise.resolve();
        }
      }
      expiredCache = expired;
    }
    const curTime = Date.now();
    if (curTime - this._lastConfigLoadTime < this._expiryTimeoutSec * 1000) {
      return Promise.resolve();
    }
    const loadPromise = this._load(info, bFirstLoad);
    return bFirstLoad && expiredCache ? loadPromise : Promise.resolve();
  }
}

// export singleton object
export default new _adconfigUpdater();
